import React, { useContext, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Download } from 'assets/svg';
import { Modal, Popover, Spin } from 'antd';
import styled from '@emotion/styled';
import {
    calculateFontSize,
    formatTimestamp,
    getConfig,
    globalFilterFlattened,
    sendNotification,
} from 'utils/helperMethods';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import dayjs from 'dayjs';
import { UserContext } from 'contexts/ConfigContext';
// import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CustomSelect } from 'components/pages/GrowthAnalysis';
import DownSvg from 'assets/svg/Down';
import { trackEventMixpanel } from 'utils/mixpanel';
import { XLSX_EXPORT_ERROR } from 'utils/mixpanelEventNames';
import { antIcon } from '../Filter/FilterApplyButton';
interface props {
    excelData: any;
    title: string;
    headers: any[];
    mixpanelEvent?: Function;
    excludeFilters?: boolean;
    completeDataWithHeaders?: any[]; // when given directly export the content present inside this array
    titleArray?: any[];
    customTimePeriodHeader?: any[];
}
const popUpWidth = calculateFontSize(window.innerWidth, 310);

const exportFileTypes = [
    { value: 'xlsx', label: '.xlsx', ext: 'xlsx' },
    { value: 'csv', label: '.csv', ext: 'csv' },
];

const PopoverStyled = styled(Popover)`
    .ant-popover-inner-content {
        padding: 7px 14px;
    }
`;
const ModalStyled = styled(Modal)`
    .ant-modal-content {
        border-radius: 10px;
    }
`;

const exportButtonClassName =
    'w-28 max-h-[32px] bg-purpleFont border border-purpleFont font-xs leading-[normal] flex justify-center items-center rounded p-2 cursor-pointer text-white';

function DownloadExcel({
    excelData = [],
    title = 'data',
    headers = [],
    mixpanelEvent,
    excludeFilters = false,
    completeDataWithHeaders = [],
    titleArray = [],
    customTimePeriodHeader,
}: props) {
    //filter context
    const { allCheckedAttribute, timeSelectionLabel, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const [open, setOpen] = useState(false);
    const [exportXlsxLoading, setExportXlsxLoading] = useState(false);
    const [selectedDropdownItem, setSelectedDropdownItem] = useState(exportFileTypes[0]);

    function closePopup() {
        setOpen(false); //close the popup
        setSelectedDropdownItem(exportFileTypes[0]); //set type to default - 1st item
    }

    const fileName = `${title}`;

    //final data = normal data + global filter data
    const finalData = useMemo(() => {
        if (completeDataWithHeaders?.length) {
            return completeDataWithHeaders;
        }
        if (excludeFilters) {
            return [headers, ...excelData];
        }
        const globalFilterData: any[][] = [['Filtered by:']];
        let timestampRange = '';

        //only add time period if custom time period is not provided (used in pvm)
        if (timePeriodSelection && !customTimePeriodHeader) {
            // if (timeSelectionLabel === 'Custom Date') {
            let customTimePeriod = JSON.parse(timePeriodSelection || '{}');
            customTimePeriod = {
                start_timestamp: dayjs.unix(customTimePeriod.start_timestamp).add(6, 'days').unix(),
                end_timestamp: dayjs.unix(customTimePeriod.end_timestamp).add(6, 'days').unix(),
            };
            timestampRange = formatTimestamp(customTimePeriod, commonConfig);
            // } else
            // timestampRange = formatTimestamp(JSON.parse(timePeriodSelection || '{}'), commonConfig);
            globalFilterData.push(['Time Period: ' + timeSelectionLabel + timestampRange]);
        }
        if (customTimePeriodHeader) {
            globalFilterData.push(customTimePeriodHeader);
        }
        const filteredData = globalFilterFlattened(allCheckedAttribute) || {};

        for (let i in filteredData) {
            const tempString = `${_.startCase(i)}: ${filteredData[i]?.join(', ')}`;
            globalFilterData.push([tempString]);
        }
        globalFilterData.push(['']); //new line

        return [...globalFilterData, ...titleArray, headers, ...excelData];
    }, [open]);

    const handleExportXlsx = async () => {
        if (exportXlsxLoading) return;
        setExportXlsxLoading(true);
        try {
            const XLSX = await import('xlsx'); //dynamic import , lazy loading package

            const wb = XLSX.utils.book_new();

            // Convert array of arrays (finalData) into a worksheet
            const ws = XLSX.utils.aoa_to_sheet(finalData);

            // Append worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, fileName);

            // Write workbook and trigger download
            const wBook = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wBook], { type: 'application/octet-stream' });
            saveAs(blob, `${fileName}.xlsx`);
        } catch (error) {
            sendNotification('error', 'Something went wrong with xlsx export');
            trackEventMixpanel(XLSX_EXPORT_ERROR, { error: String(error) });
        } finally {
            closePopup();
            setExportXlsxLoading(false);
        }
    };

    return (
        <>
            <PopoverStyled
                placement="bottomRight"
                showArrow={false}
                trigger="hover"
                content={<p className="font-xxs whitespace-nowrap">Export Data</p>}
                overlayInnerStyle={{ position: 'relative', top: -10, borderRadius: '5px', padding: '0px' }}
            >
                <div
                    onClick={() => {
                        setOpen(true);
                        //mixpanel event
                        if (mixpanelEvent) mixpanelEvent();
                    }}
                    className="h-fit"
                >
                    <Download />
                </div>
            </PopoverStyled>
            <ModalStyled
                open={open}
                footer={null}
                centered
                style={{ borderRadius: '200px' }}
                width={popUpWidth}
                onCancel={() => closePopup()}
            >
                <h1 className="font-md leading-[normal]">Export Data</h1>
                <span className="font-xs">
                    <span className="text-purpleFont">File name:</span> {fileName}
                </span>
                <CustomSelect
                    style={{ marginTop: 8 }}
                    suffixIcon={<DownSvg />}
                    value={selectedDropdownItem.value}
                    options={exportFileTypes}
                    className="dropdown-height w-full"
                    onChange={(val: any, item: any) => {
                        setSelectedDropdownItem(item);
                    }}
                />
                <div className="w-full flex mt-4 justify-between items-center">
                    <div
                        className="w-28 text-purpleFont font-xs leading-[normal] border rounded border-purpleFont p-2 text-center cursor-pointer"
                        onClick={() => closePopup()}
                    >
                        Cancel
                    </div>
                    {/* EXPORT XLSX */}
                    {selectedDropdownItem?.value === 'xlsx' ? (
                        <div onClick={handleExportXlsx} className={exportButtonClassName}>
                            {exportXlsxLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Export'}
                        </div>
                    ) : (
                        // EXPORT CSV
                        <CSVLink data={finalData} filename={`${fileName}.csv`} onClick={() => closePopup()}>
                            <div className={exportButtonClassName}>Export</div>
                        </CSVLink>
                    )}
                </div>
            </ModalStyled>
        </>
    );
}

export default DownloadExcel;
